
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import { debugLogger } from '@/utils/debugLogger';

interface MessageLimitState {
  currentCount: number;
  limit: number;
  remaining: number;
  canSend: boolean;
  isLoading: boolean;
  hoursUntilReset: number;
}

export const useMessageLimits = () => {
  const { user, profileData } = useAuth();
  const [limitState, setLimitState] = useState<MessageLimitState>({
    currentCount: 0,
    limit: -1,
    remaining: -1,
    canSend: true,
    isLoading: true,
    hoursUntilReset: 0
  });

  const fetchDailyMessageCount = useCallback(async () => {
    if (!user?.id) return 0;

    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('sender_id', user.id)
        .gte('created_at', `${today}T00:00:00.000Z`)
        .lt('created_at', `${today}T23:59:59.999Z`);

      if (error) {
        debugLogger.error('[useMessageLimits] Error fetching message count:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      debugLogger.error('[useMessageLimits] Error in fetchDailyMessageCount:', error);
      return 0;
    }
  }, [user?.id]);

  const calculateHoursUntilReset = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const diffMs = tomorrow.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60));
  };

  const checkLimits = useCallback(async () => {
    if (!user?.id || !profileData) {
      setLimitState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const currentCount = await fetchDailyMessageCount();
      
      // Criar UserProfile para o Business Rules Engine
      const userProfile = {
        id: user.id,
        plan_type: profileData.plan_type || 'sponsor_free',
        hidden: profileData.hidden || false,
        role: profileData.role
      };

      // Verificar limites usando Business Rules Engine
      const limitResult = await businessRulesEngine.validateInteractionLimits(
        userProfile,
        'daily_messages',
        currentCount
      );

      const limit = limitResult.data?.limit || -1;
      const remaining = limit === -1 ? -1 : Math.max(0, limit - currentCount);
      const canSend = limitResult.allowed;
      const hoursUntilReset = calculateHoursUntilReset();

      setLimitState({
        currentCount,
        limit,
        remaining,
        canSend,
        isLoading: false,
        hoursUntilReset
      });

      debugLogger.debug('[useMessageLimits] Limits updated:', {
        currentCount,
        limit,
        remaining,
        canSend,
        planType: userProfile.plan_type
      });

    } catch (error) {
      debugLogger.error('[useMessageLimits] Error checking limits:', error);
      setLimitState(prev => ({ ...prev, isLoading: false }));
    }
  }, [user?.id, profileData, fetchDailyMessageCount]);

  // Verificar limites na inicialização
  useEffect(() => {
    checkLimits();
  }, [checkLimits]);

  // Função para revalidar após envio de mensagem
  const refreshLimits = useCallback(() => {
    checkLimits();
  }, [checkLimits]);

  return {
    ...limitState,
    refreshLimits
  };
};
